import { ReparoApi } from './index';

export type Product = {
    productId: string;
    merchantId: string;
    name: string;
    category: string;
    subCategory?: string | null;
    sku?: string;
    price?: number;
    productType?: string;
    productGroup?: string | null;
    isActive?: boolean | null;
    weightGrams?: number;
    volumeCbm?: number;
    /** GST slab %, e.g. 18. `null` means not stated — distinct from 0%, a real slab. */
    gstRate?: number | null;
    hsnCode?: string | null;
    /** Units in one case. Null = no case pack on file; quantity boxes then offer units only. */
    unitsPerCase?: number | null;
};

/**
 * Body for creating a product. There is no `productId` field — the backend derives
 * it from `sku`, so one SKU value lands in both columns.
 */
export type CreateProductPayload = {
    sku: string;
    name: string;
    category: string;
    subCategory?: string;
    price?: number;
    productType?: string;
    productGroup?: string;
    weightGrams?: number;
    volumeCbm?: number;
    gstRate?: number;
    hsnCode?: string;
    unitsPerCase?: number;
};

/** ADMIN only. Rejects with 409 if the SKU is already taken. */
export const createProduct = async (
    payload: CreateProductPayload,
): Promise<Product> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/products',
        body: JSON.stringify(payload),
    });
};

export type BulkCreateProductsError = {
    row: number;
    sku: string;
    reason: string;
};

export type BulkCreateProductsResult = {
    totalRows: number;
    /** Rows created — or, on a dry run, rows that *would* be created. */
    created: number;
    /** Rows whose SKU already exists. Create-only: these are never updated. */
    skipped: number;
    /** Rows that existed and were changed. Only the upsert importer sets this. */
    updated?: number;
    errors: BulkCreateProductsError[];
    dryRun: boolean;
};

/** CSV columns the importer understands, in template order. */
export const PRODUCT_CSV_COLUMNS = [
    'sku',
    'name',
    'category',
    'sub_category',
    'price',
    'product_type',
    'product_group',
    'weight_grams',
    'volume_cbm',
    'gst_rate',
    'hsn_code',
] as const;

/** Of those, the ones a row cannot omit. */
export const PRODUCT_CSV_REQUIRED_COLUMNS = ['sku', 'name', 'category'] as const;

/**
 * ADMIN only. With `dryRun` the backend validates and counts without writing —
 * used to preview an import before committing it.
 */
export const bulkCreateProducts = async (
    file: File,
    dryRun: boolean,
): Promise<BulkCreateProductsResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return ReparoApi<any>({
        method: 'POST',
        url: `/products/bulk-create?dryRun=${dryRun}`,
        body: formData,
    });
};

/**
 * Columns the upsert importer understands — the download's own header line.
 *
 * Led by `product_id` because that is what an edited row is matched on, and carrying
 * `is_active` and `brand`, which the create contract has no use for: nothing is born
 * deactivated, and a hand-written file has no export to be stamped by.
 */
export const PRODUCT_UPSERT_CSV_COLUMNS = [
    'product_id',
    'name',
    'sku',
    'category',
    'sub_category',
    'product_group',
    'product_type',
    'price',
    'weight_grams',
    'volume_cbm',
    'gst_rate',
    'hsn_code',
    'is_active',
    'brand',
] as const;

/**
 * Only the key. Everything else is optional because a blank cell means "leave this alone" —
 * the common edit is one column on a handful of rows, not a complete row rewritten.
 */
export const PRODUCT_UPSERT_REQUIRED_COLUMNS = ['product_id'] as const;

/**
 * ADMIN only. Updates rows whose `product_id` exists and creates the ones that do not.
 * With `dryRun` the backend runs every check and writes nothing.
 */
export const bulkUpsertProducts = async (
    file: File,
    dryRun: boolean,
): Promise<BulkCreateProductsResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return ReparoApi<any>({
        method: 'POST',
        url: `/products/bulk-upsert?dryRun=${dryRun}`,
        body: formData,
    });
};

export const getProductCategories = async (): Promise<string[]> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/products/categories/list',
    });
};

export const getProductTypes = async (): Promise<string[]> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/products/types/list',
    });
};

export const getProductGroups = async (): Promise<string[]> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/products/product-groups/list',
    });
};

/**
 * One parent → child edge of the merchant's product relation graph — a combo and its
 * components, a customisation variant and its base, a clearance listing and the SKU it is a
 * second-quality copy of. All three live in one table and behave identically here.
 */
export interface ProductRelationEdge {
    parentId: string;
    childId: string;
    quantity: number;
    childName: string | null;
    childSku: string | null;
    childCategory: string | null;
    childProductGroup: string | null;
    childTier: string | null;
}

/**
 * The whole graph for the merchant, unfiltered on purpose — a parent on the caller's current
 * page can explode into a child that is not on it, so a partial graph loses units. Small
 * (~400 rows for basil) and only changes with the catalogue, so callers cache it.
 */
export const getProductRelations = async (): Promise<{ edges: ProductRelationEdge[] }> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/products/relations/list',
    });
};
